import { Link } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SectionLabel } from "../components/SectionLabel";

const PRODUCTS = [
    "Vinyl Banners",
    "Event Banners",
    "Posters",
    "Wall Murals & Wraps",
    "Window Graphics",
    "Floor Graphics",
    "Decals & Stickers",
    "Backlit Prints",
    "Trade Show Graphics",
    "Canvas Prints",
];

export default function PrintShop() {
    return (
        <section id="print" data-testid="print-shop-section" className="scroll-mt-20 bg-ink">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-24 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:py-36">
                <div className="flex flex-col justify-center lg:col-span-5">
                    <Reveal>
                        <SectionLabel index="CH.04">The Print Shop</SectionLabel>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-8 font-display text-3xl font-extrabold uppercase leading-[1.12] tracking-tight text-paper sm:text-5xl sm:leading-[1.08]">
                            Photographic-quality
                            <br />
                            large-format printing.
                            <br />
                            <span className="text-acid">No install required.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-6 text-base leading-relaxed text-fog sm:text-lg">
                            You don't have to book a build to buy from us. Our Epson SureColor
                            S80600 is a 64-inch, 10-color photographic printer — the same
                            photographic-quality machine behind our murals and wraps — and it's available
                            for your job. Send the file, we ship the print — anywhere in the U.S.
                            and international.
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <ul className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                            {PRODUCTS.map((p) => (
                                <li
                                    key={p}
                                    className="flex items-start gap-3 text-sm text-paper/85"
                                >
                                    <Plus
                                        className="mt-1 h-4 w-4 shrink-0 text-acid"
                                        aria-hidden="true"
                                    />
                                    {p}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={0.4}>
                        <div className="mt-10 flex flex-wrap items-center gap-6">
                            <a
                                href="#contact"
                                data-testid="print-shop-quote-cta"
                                className="group flex w-fit items-center gap-3 border border-acid px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-acid transition-colors duration-300 hover:bg-acid hover:text-ink"
                            >
                                Get a Print Quote
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                            </a>
                            <Link
                                to="/print"
                                data-testid="print-shop-menu-link"
                                className="font-mono text-xs uppercase tracking-[0.25em] text-paper/60 transition-colors duration-300 hover:text-acid"
                            >
                                See the print menu →
                            </Link>
                        </div>
                    </Reveal>
                </div>

                <Reveal className="lg:col-span-7" y={40}>
                    <div className="relative overflow-hidden">
                        <span className="absolute left-3 top-3 z-10 h-5 w-5 border-l-2 border-t-2 border-acid" aria-hidden="true" />
                        <span className="absolute right-3 top-3 z-10 h-5 w-5 border-r-2 border-t-2 border-acid" aria-hidden="true" />
                        <span className="absolute bottom-3 left-3 z-10 h-5 w-5 border-b-2 border-l-2 border-acid" aria-hidden="true" />
                        <span className="absolute bottom-3 right-3 z-10 h-5 w-5 border-b-2 border-r-2 border-acid" aria-hidden="true" />
                        <img
                            data-testid="print-shop-image"
                            src="/images/print-counter.jpg"
                            alt="Large-format printed cash-wrap counter produced by Triple O — full-color sunset landscape vinyl wrap with razor-sharp photographic detail"
                            className="aspect-[4/3] w-full object-cover"
                            loading="lazy"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
                        <p className="absolute bottom-5 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-paper/70">
                            Printed in-house on the Epson S80600
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
